import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, BadgeCheck, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const CATS = [["cleanliness", "Cleanliness"], ["comfort", "Comfort"], ["location", "Location"],
  ["facilities", "Facilities"], ["value", "Value for money"], ["overall", "Overall experience"]];

function RatingRow({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-stone-700">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" onClick={() => onChange(i)}>
            <Star size={20} className={i <= value ? "fill-gold text-gold" : "text-stone-300"} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReviewSubmit() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [cats, setCats] = useState({ cleanliness: 5, comfort: 5, location: 5, facilities: 5, value: 5, overall: 5 });
  const [form, setForm] = useState({ title: "", text: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/review-token/${token}`)
      .then((r) => { setInfo(r.data); setState("ready"); })
      .catch((e) => { setState("error"); toast.error(formatApiError(e.response?.data?.detail)); });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const rating = Math.round(Object.values(cats).reduce((a, b) => a + b, 0) / 6);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/review-token/${token}`, { rating, title: form.title, text: form.text, categories: cats, photos: [] });
      setState("done");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-cream pt-24 pb-16 px-5">
      <div className="max-w-xl mx-auto">
        {state === "loading" && <div className="text-center py-20"><Loader2 className="animate-spin mx-auto text-rose" /></div>}
        {state === "error" && (
          <div className="card-lux p-10 text-center">
            <h1 className="font-serif text-3xl text-forest">Link unavailable</h1>
            <p className="text-stone-500 mt-3">This review link is invalid, expired or already used.</p>
          </div>
        )}
        {state === "done" && (
          <div className="card-lux p-10 text-center" data-testid="review-done">
            <BadgeCheck className="mx-auto text-green-600" size={48} />
            <h1 className="font-serif text-3xl text-forest mt-4">Thank you!</h1>
            <p className="text-stone-500 mt-3">Your verified review has been submitted and will appear once approved by our team.</p>
          </div>
        )}
        {state === "ready" && info && (
          <form onSubmit={submit} className="card-lux p-8" data-testid="review-form">
            <span className="eyebrow">Verified Stay</span>
            <h1 className="font-serif text-3xl text-forest mt-2">How was {info.room_name}?</h1>
            <p className="text-sm text-stone-500 mt-2">Hi {info.guest_name}, thank you for staying with us. Share your experience below.</p>
            <div className="mt-6 divide-y divide-stone-100">
              {CATS.map(([k, label]) => <RatingRow key={k} label={label} value={cats[k]} onChange={(v) => setCats({ ...cats, [k]: v })} />)}
            </div>
            <input required placeholder="Review title" data-testid="review-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm mt-5 focus:outline-none focus:ring-2 focus:ring-rose/30" />
            <textarea required rows={5} placeholder="Tell us about your stay..." data-testid="review-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
              className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm mt-4 focus:outline-none focus:ring-2 focus:ring-rose/30" />
            <button type="submit" disabled={submitting} data-testid="review-submit" className="btn-primary w-full mt-5 disabled:opacity-60">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : "Submit verified review"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
